import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  Eye,
  FileText,
  History,
  Lock,
  Pencil,
  ShieldCheck,
  Trash2,
  Upload,
  type LucideIcon,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { Cargando } from '@/shared/ui/Cargando';
import { ConfirmarEliminacion } from '@/shared/ui/ConfirmarEliminacion';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Interruptor } from '@/shared/ui/Interruptor';
import { AccionesDelFormulario, Campo, Modal, claseEntrada } from '@/shared/ui/Modal';
import { VistaPreviaDeImpresion } from '@/shared/ui/VistaPreviaDeImpresion';
import { notificar } from '@/shared/ui/avisos';
import { useSesion } from '@/shared/sesion/SesionProvider';
import type {
  DatosDeDocumento,
  DocumentoLegal,
  DocumentosLegales,
  TipoDeDocumentoLegal,
} from './contrato';
import { enFecha, huellaCorta, problemaDelPdf } from './documentos';
import { enPeso } from './imagenes';
import { PestanasDelPortal } from './PestanasDelPortal';

const CLAVE = ['portal', 'documentos-legales'];

const ICONOS: Record<string, LucideIcon> = {
  'politica-de-privacidad': ShieldCheck,
  'terminos-del-boton-de-pagos': CreditCard,
};

interface Vista {
  titulo: string;
  subtitulo: string;
  fichero: Blob | null;
}

export function PaginaDocumentosLegales() {
  const cliente = useQueryClient();
  const { puede } = useSesion();
  const [subiendoA, setSubiendoA] = useState<TipoDeDocumentoLegal | null>(null);
  const [editando, setEditando] = useState<DocumentoLegal | null>(null);
  const [aPublicar, setAPublicar] = useState<DocumentoLegal | null>(null);
  const [aEliminar, setAEliminar] = useState<DocumentoLegal | null>(null);
  const [vista, setVista] = useState<Vista | null>(null);

  const consulta = useQuery({
    queryKey: CLAVE,
    queryFn: () => api.get<DocumentosLegales>('/portal/documentos-legales'),
  });
  const tipos = consulta.data?.tipos ?? [];
  const documentos = consulta.data?.documentos ?? [];

  function refrescar() {
    void cliente.invalidateQueries({ queryKey: CLAVE });
  }

  function alFallar(causa: unknown, respaldo: string) {
    notificar.error(causa instanceof ErrorDeApi ? causa.message : respaldo);
  }

  const publicar = useMutation({
    mutationFn: (d: DocumentoLegal) =>
      api.post<DocumentoLegal>(`/portal/documentos-legales/${d.id}/publicar`),
    onSuccess: (d) => {
      setAPublicar(null);
      refrescar();
      notificar.exito(`Versión ${d.version} publicada`);
    },
    onError: (causa) => alFallar(causa, 'No se pudo publicar'),
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/portal/documentos-legales/${id}`),
    onSuccess: () => {
      setAEliminar(null);
      refrescar();
      notificar.exito('Borrador eliminado');
    },
    onError: (causa) => alFallar(causa, 'No se pudo eliminar'),
  });

  async function verDelServidor(d: DocumentoLegal, tipo: TipoDeDocumentoLegal | undefined) {
    const titulo = tipo?.nombre ?? d.titulo;
    const subtitulo = `Versión ${d.version} · ${d.nombreArchivo}`;
    setVista({ titulo, subtitulo, fichero: null });
    try {
      const fichero = await api.descargar(`/portal/documentos-legales/${d.id}/archivo`);

      setVista((actual) =>
        actual && actual.subtitulo === subtitulo ? { ...actual, fichero } : actual,
      );
    } catch (causa) {
      setVista(null);
      alFallar(causa, 'No se pudo abrir el documento');
    }
  }

  return (
    <>
      <EncabezadoDePagina
        icono={FileText}
        color="esmeralda"
        titulo="Portal ciudadano"
        subtitulo="Los documentos legales de la ventanilla"
      />

      <PestanasDelPortal />

      <div className="mb-5 flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <Lock className="mt-0.5 size-4 shrink-0 text-blue-600" aria-hidden />
        <p className="text-xs uppercase leading-relaxed text-blue-800">
          <span className="font-semibold">Suba el PDF firmado, revíselo y publíquelo.</span> La
          ventanilla enseña la versión vigente de cada uno, y el ciudadano la aceptará antes de
          pagar en línea. Lo publicado ya no se edita ni se borra: para cambiar el texto, suba una
          versión nueva.
        </p>
      </div>

      {consulta.isPending ? (
        <Cargando titulo="Cargando documentos" detalle="Obteniendo versiones" alto="h-48" />
      ) : (
        <div className="grid gap-6 xl:grid-cols-2">
          {tipos.map((tipo) => (
            <FichaDelTipo
              key={tipo.clave}
              tipo={tipo}
              versiones={documentos.filter((d) => d.tipo === tipo.clave)}
              puedeSubir={puede('portal.crear')}
              puedeEditar={puede('portal.editar')}
              puedeEliminar={puede('portal.eliminar')}
              alSubir={() => setSubiendoA(tipo)}
              alVer={(d) => void verDelServidor(d, tipo)}
              alPublicar={setAPublicar}
              alEditar={setEditando}
              alEliminar={setAEliminar}
            />
          ))}
        </div>
      )}

      {subiendoA && (
        <DialogoDeSubida
          tipo={subiendoA}
          alCerrar={() => setSubiendoA(null)}
          alVer={(fichero, nombre) =>
            setVista({ titulo: subiendoA.nombre, subtitulo: `Sin subir · ${nombre}`, fichero })
          }
          alSubir={refrescar}
        />
      )}

      {editando && (
        <DialogoDeEdicion
          documento={editando}
          alCerrar={() => setEditando(null)}
          alGuardar={refrescar}
        />
      )}

      <ConfirmarPublicacion
        documento={aPublicar}
        vigente={
          aPublicar
            ? documentos.find((d) => d.tipo === aPublicar.tipo && d.estado === 'VIGENTE')
            : undefined
        }
        publicando={publicar.isPending}
        alCerrar={() => setAPublicar(null)}
        alConfirmar={() => aPublicar && publicar.mutate(aPublicar)}
      />

      <ConfirmarEliminacion
        abierto={aEliminar !== null}
        alCerrar={() => setAEliminar(null)}
        alConfirmar={() => aEliminar && eliminar.mutate(aEliminar.id)}
        eliminando={eliminar.isPending}
        titulo="Eliminar borrador"
        descripcion="Se borra este borrador y su PDF. Nunca estuvo publicado, así que nadie lo ha visto en la ventanilla."
        detalle={
          aEliminar ? `Versión ${aEliminar.version} · ${aEliminar.titulo}`.toUpperCase() : undefined
        }
        error={null}
      />

      <VistaPreviaDeImpresion
        abierto={vista !== null}
        alCerrar={() => setVista(null)}
        titulo={vista?.titulo ?? ''}
        subtitulo={vista?.subtitulo}
        fichero={vista?.fichero ?? null}
        cargando={vista !== null && vista.fichero === null}
        textoDeCarga="Abriendo el documento…"
        formato="hoja"
        conBarraDelVisor
      />
    </>
  );
}

function FichaDelTipo({
  tipo,
  versiones,
  puedeSubir,
  puedeEditar,
  puedeEliminar,
  alSubir,
  alVer,
  alPublicar,
  alEditar,
  alEliminar,
}: {
  tipo: TipoDeDocumentoLegal;
  versiones: DocumentoLegal[];
  puedeSubir: boolean;
  puedeEditar: boolean;
  puedeEliminar: boolean;
  alSubir: () => void;
  alVer: (d: DocumentoLegal) => void;
  alPublicar: (d: DocumentoLegal) => void;
  alEditar: (d: DocumentoLegal) => void;
  alEliminar: (d: DocumentoLegal) => void;
}) {
  const Icono = ICONOS[tipo.clave] ?? FileText;
  const vigente = versiones.find((d) => d.estado === 'VIGENTE');
  const otras = versiones.filter((d) => d.estado !== 'VIGENTE');

  return (
    <section className="flex flex-col rounded-2xl border border-armazon-200 bg-white shadow-sm">
      <header className="flex items-start gap-4 border-b border-armazon-100 p-5">
        <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-marca-50 text-marca-700 ring-1 ring-marca-100">
          <Icono className="size-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-bold uppercase tracking-wide text-armazon-900">
            {tipo.nombre}
          </h2>
          <p className="mt-0.5 text-xs uppercase text-armazon-500">{tipo.descripcion}</p>
        </div>
        {puedeSubir && (
          <button
            type="button"
            onClick={alSubir}
            className="flex shrink-0 items-center gap-2 rounded-lg bg-marca-600 px-3 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-marca-700"
          >
            <Upload className="size-4" aria-hidden />
            Subir versión
          </button>
        )}
      </header>

      <div className="p-5">
        {vigente ? (
          <div className="rounded-xl border border-marca-200 bg-marca-50/40 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Estado estado="VIGENTE" />
              <span className="importe text-sm font-bold text-armazon-900">
                Versión {vigente.version}
              </span>
            </div>
            <p className="mt-2 text-sm font-semibold uppercase text-armazon-800">
              {vigente.titulo}
            </p>
            <Detalles documento={vigente} />
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => alVer(vigente)}
                className="flex items-center gap-2 rounded-lg border border-armazon-200 bg-white px-3 py-2 text-xs font-bold uppercase tracking-wide text-armazon-700 transition-colors hover:border-marca-300 hover:text-marca-700"
              >
                <Eye className="size-4" aria-hidden />
                Vista previa
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden />
            <p className="text-xs uppercase leading-relaxed text-amber-900">
              <span className="font-semibold">Sin versión vigente.</span> La ventanilla no puede
              enseñar este documento
              {versiones.length > 0
                ? '. Revise el borrador y publíquelo.'
                : '. Suba el PDF firmado para empezar.'}
            </p>
          </div>
        )}

        {otras.length > 0 && (
          <div className="mt-5">
            <h3 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-armazon-400">
              <History className="size-3.5" aria-hidden />
              Otras versiones
            </h3>
            <ul className="mt-2 divide-y divide-armazon-100 rounded-xl border border-armazon-200">
              {otras.map((d) => (
                <li key={d.id} className="flex flex-wrap items-center gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Estado estado={d.estado} />
                      <span className="importe text-xs font-bold text-armazon-900">
                        Versión {d.version}
                      </span>
                    </div>
                    <p
                      className="mt-1 truncate text-xs uppercase text-armazon-600"
                      title={d.titulo}
                    >
                      {d.titulo}
                    </p>
                    <p className="mt-0.5 text-[11px] uppercase text-armazon-400">
                      {d.estado === 'ANTERIOR' && d.publicadoEn
                        ? `Vigente hasta ${d.retiradoEn ? new Date(d.retiradoEn).toLocaleDateString('es-EC') : '—'}`
                        : `Subido por ${d.creadoPor ?? 'sistema'} el ${new Date(d.creadoEn).toLocaleDateString('es-EC')}`}{' '}
                      · {enPeso(d.peso)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <BotonDeIcono
                      icono={Eye}
                      etiqueta={`Vista previa de la versión ${d.version}`}
                      alPulsar={() => alVer(d)}
                    />
                    {puedeEditar && (
                      <BotonDeIcono
                        icono={CheckCircle2}
                        etiqueta={`Publicar la versión ${d.version}`}
                        alPulsar={() => alPublicar(d)}
                        tono="marca"
                      />
                    )}
                    {puedeEditar && d.estado === 'BORRADOR' && (
                      <BotonDeIcono
                        icono={Pencil}
                        etiqueta={`Editar la versión ${d.version}`}
                        alPulsar={() => alEditar(d)}
                      />
                    )}
                    {puedeEliminar && d.estado === 'BORRADOR' && (
                      <BotonDeIcono
                        icono={Trash2}
                        etiqueta={`Eliminar la versión ${d.version}`}
                        alPulsar={() => alEliminar(d)}
                        tono="alerta"
                      />
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

function Detalles({ documento: d }: { documento: DocumentoLegal }) {
  return (
    <dl className="mt-3 grid gap-x-6 gap-y-1.5 text-xs sm:grid-cols-2">
      <Dato nombre="Aprobado el" valor={enFecha(d.aprobadoEl)} />
      <Dato
        nombre="Publicado"
        valor={
          d.publicadoEn
            ? `${new Date(d.publicadoEn).toLocaleString('es-EC')} · ${d.publicadoPor ?? 'sistema'}`
            : '—'
        }
      />
      <Dato nombre="Archivo" valor={`${d.nombreArchivo} · ${enPeso(d.peso)}`} />

      <Dato nombre="Huella SHA-256" valor={huellaCorta(d.huella)} titulo={d.huella} importe />
    </dl>
  );
}

function Dato({
  nombre,
  valor,
  titulo,
  importe = false,
}: {
  nombre: string;
  valor: string;
  titulo?: string;
  importe?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-bold uppercase tracking-wider text-armazon-400">{nombre}</dt>
      <dd
        className={`truncate text-armazon-700 ${importe ? 'importe' : 'uppercase'}`}
        title={titulo ?? valor}
      >
        {valor}
      </dd>
    </div>
  );
}

const ESTADOS: Record<DocumentoLegal['estado'], { texto: string; clase: string }> = {
  VIGENTE: { texto: 'Vigente', clase: 'bg-marca-600 text-white' },
  BORRADOR: { texto: 'Borrador', clase: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200' },
  ANTERIOR: { texto: 'Anterior', clase: 'bg-armazon-100 text-armazon-500 ring-1 ring-armazon-200' },
};

function Estado({ estado }: { estado: DocumentoLegal['estado'] }) {
  const { texto, clase } = ESTADOS[estado];
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${clase}`}
    >
      {texto}
    </span>
  );
}

function BotonDeIcono({
  icono: Icono,
  etiqueta,
  alPulsar,
  tono = 'neutro',
}: {
  icono: LucideIcon;
  etiqueta: string;
  alPulsar: () => void;
  tono?: 'neutro' | 'marca' | 'alerta';
}) {
  const colores = {
    neutro: 'hover:bg-blue-50 hover:text-blue-600',
    marca: 'hover:bg-marca-50 hover:text-marca-700',
    alerta: 'hover:bg-red-50 hover:text-alerta',
  }[tono];
  return (
    <button
      type="button"
      onClick={alPulsar}
      title={etiqueta}
      aria-label={etiqueta}
      className={`rounded-lg p-1.5 text-armazon-400 transition-colors ${colores}`}
    >
      <Icono className="size-[15px]" aria-hidden />
    </button>
  );
}

function DialogoDeSubida({
  tipo,
  alCerrar,
  alVer,
  alSubir,
}: {
  tipo: TipoDeDocumentoLegal;
  alCerrar: () => void;
  alVer: (fichero: Blob, nombre: string) => void;
  alSubir: () => void;
}) {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [titulo, setTitulo] = useState(tipo.nombre);
  const [version, setVersion] = useState('');
  const [aprobadoEl, setAprobadoEl] = useState('');
  const [publicarYa, setPublicarYa] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const listo = archivo !== null && titulo.trim() !== '' && version.trim() !== '';

  async function elegir(elegido: File | undefined) {
    setError(null);
    setArchivo(null);
    if (!elegido) {
      return;
    }
    const problema = await problemaDelPdf(elegido);
    if (problema) {
      setError(problema);
      return;
    }
    setArchivo(elegido);
  }

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (!listo) {
      return;
    }
    setError(null);
    setEnviando(true);
    const formulario = new FormData();
    formulario.append('tipo', tipo.clave);
    formulario.append('titulo', titulo.trim());
    formulario.append('version', version.trim());
    if (aprobadoEl) {
      formulario.append('aprobadoEl', aprobadoEl);
    }
    formulario.append('publicar', String(publicarYa));
    formulario.append('archivo', archivo, archivo.name);
    try {
      const subido = await api.subir<DocumentoLegal>('/portal/documentos-legales', formulario);
      alSubir();
      alCerrar();
      notificar.exito(
        subido.estado === 'VIGENTE'
          ? `Versión ${subido.version} subida y publicada`
          : `Versión ${subido.version} subida como borrador`,
      );
    } catch (causa) {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo subir el documento';
      setError(mensaje);
      notificar.error(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo="Subir versión"
      subtitulo={tipo.nombre}
      icono={Upload}
      ancho="ancho"
      cerrarAlPulsarFuera={false}
    >
      <form onSubmit={enviar} className="space-y-4">
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
          >
            {error}
          </p>
        )}

        <Campo etiqueta="Archivo PDF" requerido ayuda="EL PDF FIRMADO. HASTA 8 MB.">
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-xl border border-dashed border-armazon-300 p-4 transition-colors hover:border-marca-400 hover:bg-marca-50/40">
              <FileText className="size-5 shrink-0 text-armazon-400" aria-hidden />
              <span className="min-w-0 truncate text-sm uppercase text-armazon-600">
                {archivo ? `${archivo.name} · ${enPeso(archivo.size)}` : 'Elegir el PDF'}
              </span>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="sr-only"
                onChange={(e) => void elegir(e.target.files?.[0])}
              />
            </label>
            {archivo && (
              <button
                type="button"
                onClick={() => alVer(archivo, archivo.name)}
                className="flex items-center gap-2 rounded-lg border border-armazon-200 px-3 py-2 text-xs font-bold uppercase tracking-wide text-armazon-700 transition-colors hover:border-marca-300 hover:text-marca-700"
              >
                <Eye className="size-4" aria-hidden />
                Vista previa
              </button>
            )}
          </div>
        </Campo>

        <Campo etiqueta="Título" requerido>
          <input
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            className={`${claseEntrada} uppercase`}
            maxLength={200}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Versión" requerido ayuda="LA QUE LE PONE EPAGAL: 2026, 1.0…">
            <input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              className={claseEntrada}
              maxLength={30}
              placeholder="2026"
            />
          </Campo>
          <Campo etiqueta="Aprobado o firmado el" ayuda="OPCIONAL. SOLO INFORMATIVO.">
            <input
              type="date"
              value={aprobadoEl}
              onChange={(e) => setAprobadoEl(e.target.value)}
              className={claseEntrada}
            />
          </Campo>
        </div>

        <div className="flex items-start gap-3 rounded-xl border border-armazon-200 p-3">
          <span className="mt-0.5 shrink-0">
            <Interruptor
              activo={publicarYa}
              alCambiar={() => setPublicarYa((v) => !v)}
              etiqueta="Publicarla al subirla"
            />
          </span>
          <span>
            <span className="block text-sm font-medium uppercase text-armazon-900">
              Publicarla al subirla
            </span>
            <span className="block text-xs uppercase text-armazon-500">
              Solo si ya la revisó en la vista previa. Si no, queda en borrador y la publica
              después.
            </span>
          </span>
        </div>

        <AccionesDelFormulario
          alCancelar={alCerrar}
          enviando={enviando || !listo}
          textoEnviar={publicarYa ? 'Subir y publicar' : 'Subir borrador'}
        />
      </form>
    </Modal>
  );
}

function DialogoDeEdicion({
  documento,
  alCerrar,
  alGuardar,
}: {
  documento: DocumentoLegal;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const [titulo, setTitulo] = useState(documento.titulo);
  const [version, setVersion] = useState(documento.version);
  const [aprobadoEl, setAprobadoEl] = useState(documento.aprobadoEl ?? '');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setError(null);
    setEnviando(true);
    const datos: DatosDeDocumento = {
      titulo: titulo.trim(),
      version: version.trim(),
      aprobadoEl: aprobadoEl || null,
    };
    try {
      await api.put<DocumentoLegal>(`/portal/documentos-legales/${documento.id}`, datos);
      alGuardar();
      alCerrar();
      notificar.exito('Borrador actualizado');
    } catch (causa) {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar';
      setError(mensaje);
      notificar.error(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo="Editar borrador"
      subtitulo={documento.nombreArchivo}
      icono={Pencil}
      ancho="medio"
    >
      <form onSubmit={enviar} className="space-y-4">
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
          >
            {error}
          </p>
        )}
        <Campo etiqueta="Título" requerido>
          <input
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            className={`${claseEntrada} uppercase`}
            maxLength={200}
          />
        </Campo>
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Versión" requerido>
            <input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              className={claseEntrada}
              maxLength={30}
            />
          </Campo>
          <Campo etiqueta="Aprobado o firmado el">
            <input
              type="date"
              value={aprobadoEl}
              onChange={(e) => setAprobadoEl(e.target.value)}
              className={claseEntrada}
            />
          </Campo>
        </div>
        <AccionesDelFormulario
          alCancelar={alCerrar}
          enviando={enviando || titulo.trim() === '' || version.trim() === ''}
          textoEnviar="Guardar"
        />
      </form>
    </Modal>
  );
}

function ConfirmarPublicacion({
  documento,
  vigente,
  publicando,
  alCerrar,
  alConfirmar,
}: {
  documento: DocumentoLegal | null;
  vigente: DocumentoLegal | undefined;
  publicando: boolean;
  alCerrar: () => void;
  alConfirmar: () => void;
}) {
  return (
    <Modal
      abierto={documento !== null}
      alCerrar={alCerrar}
      titulo="Publicar versión"
      icono={CheckCircle2}
      ancho="estrecho"
    >
      {documento && (
        <div className="space-y-3 text-sm text-armazon-700">
          <p>
            La ventanilla enseñará la{' '}
            <span className="font-semibold text-armazon-900">versión {documento.version}</span>.
          </p>
          {vigente && (
            <p>
              La versión <span className="font-semibold">{vigente.version}</span>, vigente hoy,
              pasará al historial.
            </p>
          )}
          <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs uppercase text-amber-900">
            <Lock className="mt-0.5 size-4 shrink-0" aria-hidden />
            Una vez publicada no se podrá editar ni borrar. Revísela antes en la vista previa.
          </p>
        </div>
      )}
      <div className="mt-6 flex items-center justify-end gap-2 border-t border-armazon-100 pt-5">
        <button
          type="button"
          onClick={alCerrar}
          className="rounded-lg px-4 py-2 text-sm font-semibold uppercase text-armazon-600 transition-colors hover:bg-armazon-100"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={alConfirmar}
          disabled={publicando}
          className="flex items-center gap-2 rounded-lg bg-marca-600 px-4 py-2 text-sm font-bold uppercase text-white transition-colors hover:bg-marca-700 disabled:opacity-60"
        >
          <CheckCircle2 className="size-4" aria-hidden />
          {publicando ? 'Publicando…' : 'Publicar'}
        </button>
      </div>
    </Modal>
  );
}
